import { useState, useEffect } from "react";
import axios from "axios";
import AdminPasswordGenerator from "./AdminPasswordGenerator";
import ContentManagement from "./ContentManagement";
import StudentManagement from "./StudentManagement";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminDashboard({ user, config }) {
  const [activeAdminTab, setActiveAdminTab] = useState("overview");
  const [stats, setStats] = useState({
    totalStudents: 0,
    activeSubscriptions: 0,
    totalExams: 0,
    ordainedStudents: 0
  });
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [emailStatus, setEmailStatus] = useState(null);
  const [sendingEmails, setSendingEmails] = useState(false);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data.stats);
      setStudents(response.data.students);
      setError("");
    } catch (error) {
      console.error("Error fetching admin data:", error);
      setError("Error loading dashboard data. Please refresh.");
    } finally {
      setLoading(false);
    }
  };

  const sendWeeklyEmails = async () => {
    try {
      setSendingEmails(true);
      setEmailStatus(null);
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/admin/send-weekly-emails`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailStatus({
        success: true,
        message: `Weekly emails sent to ${response.data.sent} students`
      });
    } catch (error) {
      console.error("Error sending weekly emails:", error);
      setEmailStatus({
        success: false,
        message: "Failed to send weekly emails"
      });
    } finally {
      setSendingEmails(false);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <h3>Admin Dashboard</h3>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="card" data-testid="admin-dashboard">
        <h3>🔐 Admin Dashboard - Chief Priest YKBN</h3>
        <p style={{ color: "#FFD700", marginBottom: "20px" }}>
          Yisrael Learning Center Management Portal
        </p>

        {error && <div className="alert alert-error">{error}</div>}

        {/* Admin Tabs */}
        <div style={{ display: "flex", gap: "10px", marginBottom: "20px", flexWrap: "wrap" }}>
          <button
            onClick={() => setActiveAdminTab("overview")}
            style={{
              background: activeAdminTab === "overview" ? "#FFD700" : "#333",
              color: activeAdminTab === "overview" ? "#111" : "#eee",
              width: "auto",
              flex: 1,
              minWidth: "120px",
              marginTop: 0
            }}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveAdminTab("content")}
            style={{
              background: activeAdminTab === "content" ? "#FFD700" : "#333",
              color: activeAdminTab === "content" ? "#111" : "#eee",
              width: "auto",
              flex: 1,
              minWidth: "120px",
              marginTop: 0
            }}
          >
            Content
          </button>
          <button
            onClick={() => setActiveAdminTab("emails")}
            style={{
              background: activeAdminTab === "emails" ? "#FFD700" : "#333",
              color: activeAdminTab === "emails" ? "#111" : "#eee",
              width: "auto",
              flex: 1,
              minWidth: "120px",
              marginTop: 0
            }}
          >
            Weekly Emails
          </button>
          <button
            onClick={() => setActiveAdminTab("passwords")}
            style={{
              background: activeAdminTab === "passwords" ? "#FFD700" : "#333",
              color: activeAdminTab === "passwords" ? "#111" : "#eee",
              width: "auto",
              flex: 1,
              minWidth: "120px",
              marginTop: 0
            }}
          >
            Passwords
          </button>
          <button
            onClick={() => setActiveAdminTab("students")}
            style={{
              background: activeAdminTab === "students" ? "#FFD700" : "#333",
              color: activeAdminTab === "students" ? "#111" : "#eee",
              width: "auto",
              flex: 1,
              minWidth: "120px",
              marginTop: 0
            }}
          >
            Students
          </button>
        </div>

        {/* Content Management Tab */}
        {activeAdminTab === "content" && <ContentManagement />}

        {/* Student Management Tab */}
        {activeAdminTab === "students" && <StudentManagement />}

        {/* Email Tab */}
        {activeAdminTab === "emails" && (
          <div className="card" style={{ background: "#1a1a1a" }}>
            <h4 style={{ color: "#FFD700" }}>Weekly Progress Email Reports</h4>
            <p style={{ color: "#aaa", marginBottom: "20px" }}>
              Send weekly progress reports to all students with email notifications enabled.
              Each student will receive their quiz scores, leaderboard rank, and progress summary.
            </p>
            
            <button
              onClick={sendWeeklyEmails}
              disabled={sendingEmails}
              style={{
                background: sendingEmails ? "#555" : "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
                color: "#000",
                padding: "15px 30px",
                fontSize: "1rem",
                fontWeight: "bold",
                border: "none",
                borderRadius: "8px",
                cursor: sendingEmails ? "not-allowed" : "pointer"
              }}
            >
              {sendingEmails ? "Sending Emails..." : "Send Weekly Emails Now"}
            </button>
            
            {emailStatus && (
              <div style={{
                marginTop: "20px",
                padding: "15px",
                borderRadius: "8px",
                background: emailStatus.success ? "#1a4d1a" : "#4d1a1a",
                border: `1px solid ${emailStatus.success ? "#90EE90" : "#ff6b6b"}`,
                color: emailStatus.success ? "#90EE90" : "#ff6b6b"
              }}>
                {emailStatus.message}
              </div>
            )}
            
            <div style={{
              marginTop: "30px",
              padding: "15px",
              background: "#111",
              borderRadius: "8px",
              color: "#888"
            }}>
              <strong style={{ color: "#FFD700" }}>Note:</strong> Weekly emails include:
              <ul style={{ marginTop: "10px", paddingLeft: "20px" }}>
                <li>Quizzes completed this week</li>
                <li>Average score</li>
                <li>Leaderboard rank</li>
                <li>Day streak status</li>
                <li>Total points earned</li>
              </ul>
            </div>
          </div>
        )}

        {/* Overview Tab */}
        {activeAdminTab === "overview" && (
          <div>

        {/* Statistics */}
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
          gap: "15px",
          marginBottom: "30px"
        }}>
          <div style={{ 
            background: "#1a4d1a", 
            padding: "20px", 
            borderRadius: "8px",
            textAlign: "center",
            border: "2px solid #90EE90"
          }}>
            <h2 style={{ margin: "0 0 5px 0", color: "#90EE90" }}>
              {stats.totalStudents}
            </h2>
            <p style={{ margin: 0, color: "#eee" }}>Total Students</p>
          </div>

          <div style={{ 
            background: "#1a3a4d", 
            padding: "20px", 
            borderRadius: "8px",
            textAlign: "center",
            border: "2px solid #87CEEB"
          }}>
            <h2 style={{ margin: "0 0 5px 0", color: "#87CEEB" }}>
              {stats.activeSubscriptions}
            </h2>
            <p style={{ margin: 0, color: "#eee" }}>Active Subscriptions</p>
          </div>

          <div style={{ 
            background: "#4d3a1a", 
            padding: "20px", 
            borderRadius: "8px",
            textAlign: "center",
            border: "2px solid #FFD700"
          }}>
            <h2 style={{ margin: "0 0 5px 0", color: "#FFD700" }}>
              {stats.ordainedStudents}
            </h2>
            <p style={{ margin: 0, color: "#eee" }}>Ordained Ministers</p>
          </div>

          <div style={{ 
            background: "#222", 
            padding: "20px", 
            borderRadius: "8px",
            textAlign: "center",
            border: "2px solid #FFD700"
          }}>
            <h2 style={{ margin: "0 0 5px 0", color: "#FFD700" }}>
              {stats.totalExams}
            </h2>
            <p style={{ margin: 0, color: "#eee" }}>Total Exams Taken</p>
          </div>
        </div>

        {/* Recent Students */}
        <div className="card" style={{ background: "#222" }}>
          <h4 style={{ color: "#FFD700", marginBottom: "15px" }}>
            Recent Students
          </h4>
          
          {students.length === 0 ? (
            <p style={{ color: "#aaa" }}>No students registered yet.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Subscription</th>
                    <th>Ordained</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student, index) => (
                    <tr key={index}>
                      <td>{student.username}</td>
                      <td style={{ fontSize: "0.9rem" }}>{student.email}</td>
                      <td>
                        <span style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "0.85rem",
                          background: student.subscription_status === "active" ? "#1a4d1a" : "#4d1a1a",
                          color: student.subscription_status === "active" ? "#90EE90" : "#FF6B6B"
                        }}>
                          {student.subscription_status}
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {student.ordained ? "✅" : "—"}
                      </td>
                      <td style={{ fontSize: "0.9rem", color: "#aaa" }}>
                        {new Date(student.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="card" style={{ background: "#1a1a1a", marginTop: "20px" }}>
          <h4 style={{ color: "#FFD700", marginBottom: "15px" }}>
            Quick Actions
          </h4>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <button 
              onClick={() => alert("Content management coming soon!")}
              style={{ width: "auto", flex: 1, minWidth: "150px" }}
            >
              📚 Manage Content
            </button>
            <button 
              onClick={() => alert("Email all students feature coming soon!")}
              style={{ width: "auto", flex: 1, minWidth: "150px" }}
            >
              ✉️ Email Students
            </button>
            <button 
              onClick={() => window.location.href = `mailto:${config?.SUPPORT_EMAIL}`}
              style={{ width: "auto", flex: 1, minWidth: "150px" }}
            >
              📧 Support Email
            </button>
          </div>
        </div>

        {/* System Info */}
        <div style={{ 
          marginTop: "20px", 
          padding: "15px", 
          background: "#0a0a0a", 
          borderRadius: "6px",
          fontSize: "0.85rem",
          color: "#aaa"
        }}>
          <p style={{ margin: "0 0 5px 0" }}>
            <strong style={{ color: "#FFD700" }}>Administrator:</strong> {user.username} (Chief Priest)
          </p>
          <p style={{ margin: "0 0 5px 0" }}>
            <strong style={{ color: "#FFD700" }}>Support Email:</strong> {config?.SUPPORT_EMAIL}
          </p>
          <p style={{ margin: "0" }}>
            <strong style={{ color: "#FFD700" }}>Platform:</strong> Yisrael Learning Center - Twelve Tribes of Yisrael
          </p>
        </div>
          </div>
        )}

        {/* Password Generator Tab */}
        {activeAdminTab === "passwords" && (
          <AdminPasswordGenerator config={config} />
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
